import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, FileText, User, Calendar, UserCheck, Shield } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi, bustCache } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import DisclaimerModal from '../components/DisclaimerModal';
import DentistPicker from '../components/DentistPicker';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const AssignInformedConsentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  // Get pre-selected patient from URL parameters
  const preSelectedPatientId = searchParams.get('patientId');
  const preSelectedPatientName = searchParams.get('patientName');
  
  const [patients, setPatients] = useState([]);
  const [consents, setConsents] = useState([]);
  const [dentists, setDentists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Disclaimer state
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [checkingDisclaimer, setCheckingDisclaimer] = useState(false);
  
  const [formData, setFormData] = useState({
    patientId: preSelectedPatientId || '',
    consentId: '',
    assignedDate: new Date().toISOString().split('T')[0],
    dentistId: '',
    dentistName: user?.firstName ? `${user.firstName} ${user.lastName}` : '',
    practiceNotes: ''
  });
  
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [errors, setErrors] = useState({});
  
  // Filter patients based on search term
  const filteredPatients = React.useMemo(() => {
    if (!patientSearchTerm) return patients;
    const searchLower = patientSearchTerm.toLowerCase();
    return patients.filter(patient => {
      const fullName = `${patient.firstName} ${patient.lastName}`.toLowerCase();
      const email = (patient.email || '').toLowerCase();
      return fullName.includes(searchLower) || email.includes(searchLower);
    });
  }, [patients, patientSearchTerm]);

  useEffect(() => {
    checkDisclaimerStatus();
  }, []);

  useEffect(() => {
    if (disclaimerAccepted) {
      loadData();
    }
  }, [disclaimerAccepted]);

  const checkDisclaimerStatus = async () => {
    console.log('🔍 [ASSIGN] checkDisclaimerStatus called');
    try {
      setCheckingDisclaimer(true);
      const token = localStorage.getItem('dentalToken');
      console.log('🔑 [ASSIGN] Token exists:', !!token);
      if (!token) {
        console.log('❌ [ASSIGN] No token, returning');
        return;
      }

      console.log('📡 [ASSIGN] Calling API:', `${backendUrl}/api/informed-consent/disclaimer/status`);
      const response = await axios.get(
        `${backendUrl}/api/informed-consent/disclaimer/status`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('📥 [ASSIGN] API Response:', response.data);
      console.log('✓ [ASSIGN] Accepted status:', response.data.accepted);

      if (response.data.accepted) {
        console.log('✅ [ASSIGN] Disclaimer already accepted - HIDING modal');
        setDisclaimerAccepted(true);
        setShowDisclaimer(false);
      } else {
        console.log('⚠️ [ASSIGN] Disclaimer not yet accepted - SHOWING modal');
        setShowDisclaimer(true);
      }
    } catch (error) {
      console.error('❌ [ASSIGN] Error checking disclaimer status:', error);
      console.error('[ASSIGN] Error details:', error.response?.data);
      // If error, show disclaimer to be safe
      console.log('🚨 [ASSIGN] Error occurred - SHOWING modal as fallback');
      setShowDisclaimer(true);
    } finally {
      setCheckingDisclaimer(false);
    }
  };

  const handleDisclaimerAccept = () => {
    // DisclaimerModal already saved to backend before calling this
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    toast({
      title: "Disclaimer Accepted",
      description: "You can now assign informed consents to patients",
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load patients and informed consents (excluding compliance refusal forms,
      // which live in the separate Compliance Forms Library)
      const [patientsResponse, consentsResponse] = await Promise.all([
        practiceApi.getPatients(),
        axios.get(`${backendUrl}/api/informed-consent/list`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('dentalToken')}` }
        })
      ]);
      
      setPatients(patientsResponse.data || []);
      const allConsents = consentsResponse.data.data || [];
      // Filter out compliance refusal forms — those have their own library / assign page.
      setConsents(allConsents.filter(c => !c.isRefusalForm && !c.isXrayRefusalForm));
      
      // Try to load dentists
      try {
        const dentistsResponse = await practiceApi.getDentists();
        setDentists(dentistsResponse.data || []);
      } catch (dentistsError) {
        console.warn('Failed to load dentists, using current user:', dentistsError);
        if (user?.firstName && user?.lastName) {
          setDentists([{
            id: user.id,
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email || '',
            phone: user.phone || '',
            specialties: []
          }]);
        }
      }
      
      console.log('Patients loaded:', patientsResponse.data?.length || 0);
      console.log('Consents loaded:', consentsResponse.data.data?.length || 0);
      console.log('Dentists loaded:', dentists.length);
      
    } catch (error) {
      console.error('Load data error:', error);
      toast({
        title: "Error",
        description: "Failed to load required data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user makes changes
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.patientId) {
      newErrors.patientId = 'Please select a patient';
    }
    
    if (!formData.consentId) {
      newErrors.consentId = 'Please select an informed consent';
    }
    
    if (!formData.assignedDate) {
      newErrors.assignedDate = 'Please select a date';
    }
    
    if (!formData.dentistName?.trim()) {
      newErrors.dentistName = 'Please enter the dentist name';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }
    
    try {
      setSubmitting(true);
      
      const selectedConsent = consents.find(c => c.id === formData.consentId);
      
      const assignmentData = {
        patientId: formData.patientId,
        consentId: formData.consentId,
        consentName: selectedConsent?.name || '',
        assignedDate: new Date(formData.assignedDate).toISOString(),
        dentistName: formData.dentistName,
        practiceNotes: formData.practiceNotes || ''
      };
      
      const token = localStorage.getItem('dentalToken');
      await axios.post(
        `${backendUrl}/api/informed-consent/assign`,
        assignmentData,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Bust the dashboard cache so new assignment shows immediately
      bustCache('dashboard');
      
      toast({
        title: "Success",
        description: "Informed consent assigned successfully",
      });
      
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Assignment error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to assign informed consent",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Show disclaimer modal if not accepted
  if (checkingDisclaimer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
        <p className="ml-4 text-gray-600">Checking disclaimer status...</p>
      </div>
    );
  }

  if (!disclaimerAccepted) {
    return (
      <DisclaimerModal
        isOpen={showDisclaimer}
        onAccept={handleDisclaimerAccept}
        onClose={() => {
          setShowDisclaimer(false);
          navigate('/dashboard');
        }}
      />
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  const selectedPatient = patients.find(p => p.id === formData.patientId);
  const selectedConsent = consents.find(c => c.id === formData.consentId);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center space-x-3">
            <Shield className="h-8 w-8 text-indigo-600" />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Assign Informed Consent
              </h1>
              <p className="text-gray-600 mt-1">
                Assign an informed consent document to a patient
              </p>
            </div>
          </div>
        </div>

        {/* Pre-selected Patient Info */}
        {preSelectedPatientName && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-900">
              <strong>Pre-selected Patient:</strong> {preSelectedPatientName}
            </p>
          </div>
        )}

        {/* Assignment Form */}
        <form onSubmit={handleSubmit}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-indigo-600" />
                <span>Assignment Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Patient Selection */}
              <div className="space-y-2">
                <Label htmlFor="patient" className="flex items-center space-x-2">
                  <User className="h-4 w-4" />
                  <span>Patient *</span>
                </Label>
                
                <div className="space-y-2">
                  {/* Always show search box if no patient selected */}
                  {!formData.patientId && (
                    <input
                      type="text"
                      placeholder="Type patient name to search..."
                      value={patientSearchTerm}
                      onChange={(e) => setPatientSearchTerm(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  )}
                  
                  {/* Selected Patient Display - ALWAYS shows when patient is selected */}
                  {formData.patientId && patients.length > 0 && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-md flex items-center justify-between">
                      <div>
                        <p className="font-medium text-blue-900">
                          {patients.find(p => p.id === formData.patientId)?.firstName}{' '}
                          {patients.find(p => p.id === formData.patientId)?.lastName}
                        </p>
                        <p className="text-sm text-blue-700">
                          {patients.find(p => p.id === formData.patientId)?.email}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setFormData(prev => ({ ...prev, patientId: '' }));
                          setPatientSearchTerm('');
                        }}
                        className="px-3 py-1 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
                      >
                        Change Patient
                      </button>
                    </div>
                  )}
                  
                  {/* Clickable Patient List */}
                  {patientSearchTerm && !formData.patientId && (
                    <div className="border border-gray-300 rounded-md max-h-64 overflow-y-auto">
                      {filteredPatients.length > 0 ? (
                        filteredPatients.map((patient) => {
                          // Check for duplicate names
                          const duplicates = filteredPatients.filter(p => 
                            p.firstName === patient.firstName && p.lastName === patient.lastName
                          );
                          const showEmail = duplicates.length > 1;
                          
                          return (
                            <div
                              key={patient.id}
                              onClick={() => {
                                handleInputChange('patientId', patient.id);
                                setPatientSearchTerm('');
                              }}
                              className="p-3 hover:bg-gray-100 cursor-pointer border-b last:border-b-0"
                            >
                              <p className="font-medium text-gray-900">
                                {patient.firstName} {patient.lastName}
                              </p>
                              {showEmail && patient.email && (
                                <p className="text-sm text-gray-600">{patient.email}</p>
                              )}
                            </div>
                          );
                        })
                      ) : (
                        <div className="p-3 text-center text-gray-500">
                          No patients match "{patientSearchTerm}"
                        </div>
                      )}
                    </div>
                  )}
                  
                  {patientSearchTerm && filteredPatients.length > 0 && !formData.patientId && (
                    <p className="text-xs text-gray-600">
                      {filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''} found - click to select
                    </p>
                  )}
                </div>
                
                {errors.patientId && (
                  <p className="text-sm text-red-600">{errors.patientId}</p>
                )}
              </div>

              {/* Informed Consent Selection */}
              <div className="space-y-2">
                <Label htmlFor="consent" className="flex items-center space-x-2">
                  <FileText className="h-4 w-4" />
                  <span>Informed Consent *</span>
                </Label>
                <Select
                  value={formData.consentId}
                  onValueChange={(value) => handleInputChange('consentId', value)}
                >
                  <SelectTrigger 
                    id="consent"
                    className={errors.consentId ? 'border-red-500' : ''}
                  >
                    <SelectValue placeholder="Select an informed consent" />
                  </SelectTrigger>
                  <SelectContent>
                    {consents.map((consent) => (
                      <SelectItem key={consent.id} value={consent.id}>
                        {consent.name} {consent.category ? `(${consent.category})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.consentId && (
                  <p className="text-sm text-red-600">{errors.consentId}</p>
                )}
                {selectedConsent && (
                  <p className="text-sm text-gray-600">
                    Category: {selectedConsent.category?.replace('-', ' ') || 'General'}
                  </p>
                )}
              </div>

              {/* Assigned Date */}
              <div className="space-y-2">
                <Label htmlFor="assignedDate" className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4" />
                  <span>Assigned Date *</span>
                </Label>
                <Input
                  id="assignedDate"
                  type="date"
                  value={formData.assignedDate}
                  onChange={(e) => handleInputChange('assignedDate', e.target.value)}
                  className={errors.assignedDate ? 'border-red-500' : ''}
                />
                {errors.assignedDate && (
                  <p className="text-sm text-red-600">{errors.assignedDate}</p>
                )}
              </div>

              {/* Dentist Name */}
              <div className="space-y-2">
                <Label htmlFor="dentist" className="flex items-center space-x-2">
                  <UserCheck className="h-4 w-4" />
                  <span>Dentist/Provider *</span>
                </Label>
                <DentistPicker
                  dentists={dentists}
                  selectedId={formData.dentistId}
                  selectedName={formData.dentistName}
                  onChange={({ id, name, dentist }) => {
                    setFormData((prev) => ({
                      ...prev,
                      dentistId: id,
                      dentistName: dentist
                        ? `${dentist.firstName} ${dentist.lastName}`
                        : name,
                    }));
                  }}
                  onDentistAdded={(d) =>
                    setDentists((prev) => {
                      const without = prev.filter((x) => x.id !== d.id);
                      return [...without, d].sort((a, b) =>
                        (a.lastName || '').localeCompare(b.lastName || '')
                      );
                    })
                  }
                  error={errors.dentistName}
                  testIdPrefix="consent"
                />
                {errors.dentistName && (
                  <p className="text-sm text-red-600">{errors.dentistName}</p>
                )}
              </div>

              {/* Practice Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Practice Notes (Optional)</Label>
                <Textarea
                  id="notes"
                  value={formData.practiceNotes}
                  onChange={(e) => handleInputChange('practiceNotes', e.target.value)}
                  placeholder="Add any internal notes about this assignment..."
                  rows={4}
                />
              </div>

              {/* Summary Box */}
              {selectedPatient && selectedConsent && (
                <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-testid="assignment-summary">
                  <h3 className="font-semibold text-gray-900 mb-2">Assignment Summary</h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p><strong>Patient:</strong> {selectedPatient.firstName} {selectedPatient.lastName}</p>
                    <p><strong>Informed Consent:</strong> {selectedConsent.name}</p>
                    <p><strong>Date:</strong> {new Date(formData.assignedDate).toLocaleDateString()}</p>
                    <p><strong>Provider:</strong> {formData.dentistName}</p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="assign-informed-consent-submit"
                >
                  {submitting ? 'Assigning...' : 'Assign Informed Consent'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/patient-management')}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>
    </div>
  );
};

export default AssignInformedConsentPage;
